import { useQuery } from "@tanstack/react-query";
import { type MeResponse, api } from "../lib/api";
import { clearTokens, startLogin } from "../lib/auth";

export function Welcome() {
	const { data, isLoading, error } = useQuery<MeResponse>({
		queryKey: ["me"],
		queryFn: () => api.get("/me"),
	});

	if (isLoading) return <div className="p-6">Loading…</div>;
	if (error) {
		return (
			<div className="p-6 space-y-3">
				<div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
					{(error as Error).message}
				</div>
				<button
					type="button"
					onClick={() => startLogin()}
					className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
				>
					Sign in
				</button>
			</div>
		);
	}
	if (!data) return null;

	return (
		<div className="p-6 space-y-6">
			<div>
				<h1 className="text-xl font-semibold">Welcome to g1.markets</h1>
				<p className="text-sm text-neutral-600">
					Signed in as <span className="font-mono">{data.user.email ?? data.user.id}</span>
				</p>
			</div>

			{data.org ? (
				<section className="rounded border border-emerald-200 bg-emerald-50 p-4">
					<div className="text-sm text-emerald-900">
						Your org: <strong>{data.org.displayName}</strong> ({data.org.kind}) · role{" "}
						<span className="font-mono">{data.org.role}</span>
					</div>
					<div className="mt-2 text-xs text-emerald-700">org_id: {data.org.id}</div>
				</section>
			) : (
				<section className="rounded border border-amber-300 bg-amber-50 p-4 space-y-2">
					<div className="text-sm font-medium text-amber-900">No org membership yet</div>
					<div className="text-sm text-amber-800">
						Run this from your terminal to bootstrap an org with you as admin:
					</div>
					<pre className="overflow-auto rounded bg-amber-100 p-3 text-xs">
						{`op run --env-file=.env.1password.markets -- bash -c '
TOKEN=$(curl -sS -X POST "https://api.turso.tech/v1/organizations/theg1team/databases/g1-markets-shared/auth/tokens?expiration=never&authorization=full-access" \\
  -H "Authorization: Bearer $TURSO_ORG_TOKEN" | python3 -c "import json,sys; print(json.load(sys.stdin)[\\"jwt\\"])")
TURSO_URL="libsql://g1-markets-shared-theg1team.aws-eu-west-1.turso.io" \\
TURSO_TOKEN="$TOKEN" \\
pnpm exec tsx tools/bootstrap-org.ts \\
  --kind brand \\
  --legal-name "My Brand Ltd" \\
  --country US \\
  --user-id ${data.user.id}
'`}
					</pre>
					<div className="text-xs text-amber-700">
						Change <code>--kind brand</code> to <code>affiliate</code> for an affiliate org.
					</div>
				</section>
			)}

			<section>
				<h2 className="mb-2 text-base font-medium">Quick actions</h2>
				<ul className="space-y-1 text-sm">
					{data.org?.kind === "brand" && (
						<li>
							<a href="/offers" className="text-blue-700 underline">
								Create your first offer
							</a>
						</li>
					)}
					{data.org?.kind === "affiliate" && (
						<li>
							<a href="/traffic" className="text-blue-700 underline">
								Publish your traffic profile
							</a>
						</li>
					)}
					<li>
						<a href="/directory" className="text-blue-700 underline">
							Browse public directory
						</a>
					</li>
					<li>
						<a href="/approvals" className="text-blue-700 underline">
							Review pending agent drafts
						</a>
					</li>
				</ul>
			</section>

			<button
				type="button"
				onClick={() => {
					clearTokens();
					location.href = "/";
				}}
				className="rounded bg-neutral-200 px-3 py-1 text-sm hover:bg-neutral-300"
			>
				Sign out
			</button>
		</div>
	);
}
